import React, { useState } from 'react';
import { Button } from '../ui/button';
import AssistantIcon from '@/components/icons/AssistantIcon';
import { submitTnc } from '@/utils/tncUtils';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpWaypoint,
  trackAdpEvent,
} from '@/services/analytics/adp';

interface TermsAndConditionsProps {
  onAccepted: () => void;
  isUpdated?: boolean;
}

export const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  onAccepted,
  isUpdated = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [acceptError, setAcceptError] = useState(false);

  const tncAttrs = { is_updated: String(isUpdated) };

  const handleAccept = async () => {
    setIsSubmitting(true);
    setAcceptError(false);
    const result = await submitTnc(true);
    setIsSubmitting(false);

    if (result?.success) {
      trackAdpWaypoint(getBernoulliAdp(), ADP_EVENTS.TNC_ACCEPT, tncAttrs);
      onAccepted();
    } else {
      trackAdpWaypoint(
        getBernoulliAdp(),
        ADP_EVENTS.TNC_ACCEPT_ERROR,
        tncAttrs,
      );
      setAcceptError(true);
    }
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    setAcceptError(false);
    setRejected(true);
    trackAdpWaypoint(getBernoulliAdp(), ADP_EVENTS.TNC_DECLINE, tncAttrs);

    submitTnc(false);

    // Keep buttons disabled briefly to prevent rapid repeated declines
    setTimeout(() => setIsSubmitting(false), 2000);
  };

  const linkClass =
    'text-foreground font-medium underline underline-offset-2 hover:opacity-70 transition-opacity';

  return (
    <div className="flex items-center justify-center h-full w-full bg-background">
      <div className="w-full max-w-4xl mx-4 flex flex-col bg-card rounded-2xl shadow-lg border border-border overflow-hidden">
        {/* Header */}
        <div className="px-8 py-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary">
              <AssistantIcon size={20} className="text-foreground" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Autodesk Assistant Preview
              </h2>
              <p className="text-sm text-muted-foreground">
                {isUpdated
                  ? 'Our terms have been updated — please review and accept to continue'
                  : 'Please review and accept the terms to continue'}
              </p>
            </div>
          </div>
          {isUpdated && (
            <div className="mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <span className="text-amber-600 text-base leading-none">!</span>
              </div>
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Terms updated
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  The terms and conditions have changed since you last accepted.
                  Please review the updated terms below.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable content */}
        <div className="px-8 py-6 overflow-y-auto max-h-[55vh] text-[13px] text-muted-foreground leading-[1.7] space-y-4">
          <p>
            Autodesk invites you to test and experience the pre-release version
            of Autodesk Assistant (collectively, &ldquo;Preview&rdquo;).
          </p>

          <p>
            Subject to the terms set forth below (the &ldquo;Preview
            Terms&rdquo;), the Preview is governed by the{' '}
            <a
              href="https://www.autodesk.com/company/terms-of-use/en/general-terms"
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
              onClick={() =>
                trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.TNC_LINK_CLICK, {
                  link: 'termsOfUse',
                })
              }>
              Autodesk Terms of Use
            </a>
            , which are incorporated by reference herein (collectively, with the
            Preview Terms, the &ldquo;Terms&rdquo;). To the extent of any
            conflict between the Preview Terms and the Autodesk Terms of Use,
            the Preview Terms will control. All terms used herein, but not
            otherwise defined in the Preview Terms, will have the meanings set
            forth in the Autodesk Terms of Use.
          </p>

          <p className="font-semibold text-foreground uppercase tracking-wide text-xs pt-2">
            Preview Terms
          </p>

          <p>
            <span className="font-semibold text-foreground">
              Your Use of the Trial Versions:
            </span>{' '}
            Any products, services, and/or features made available to You as
            part of the Preview are Trial Versions as defined in the Autodesk
            Terms of Use. You agree that you are not a competitor and will not
            use any Trial Versions provided to you by Autodesk in connection
            with the Preview for competitive analysis purposes. Notwithstanding
            anything contained in these Terms or otherwise, (a) Autodesk makes
            no commitments with respect to the Preview regarding any features,
            functions, or service levels, and provides no warranties of any kind
            with respect to this Preview; (b) Autodesk may choose not to convert
            this Preview into a product, service, and/or feature; and (c) this
            Preview may contain code that is not fully tested, including errors
            and faults that may cause inaccuracies or other issues affecting
            functionality, data, or service availability.
          </p>

          <p>
            <span className="font-semibold text-foreground">Feedback:</span> In
            accordance with the Autodesk Terms of Use, Feedback (as defined
            therein) from customers regarding the Preview will be used by
            Autodesk to improve current or future releases of Autodesk products,
            services, and/or features. For the avoidance of doubt, Feedback
            shall not be considered Confidential Information and shall not
            create any confidentiality obligation for or upon Autodesk.
          </p>

          <p>
            <span className="font-semibold text-foreground">Preview Term:</span>{' '}
            Your subscription and access to the Preview will begin on the date
            of Your acceptance of these Terms, and unless earlier terminated,
            will continue until the end of the Preview period as determined
            solely by Autodesk. Autodesk reserves the right, at its sole
            discretion, to modify, suspend, or terminate your participation in
            the Preview and these Terms at any time for any reason. You may
            terminate your participation in the Preview at any time for any
            reason.
          </p>

          <p>
            <span className="font-semibold text-foreground">
              Preview Availability:
            </span>{' '}
            Participation in the Preview may be limited by geographic location
            or jurisdiction, and availability may vary by region. Autodesk does
            not guarantee availability in all locations.
          </p>

          <p>
            By installing, downloading, accessing, or otherwise copying or using
            all or any products, services, and/or features under the Preview,
            you acknowledge and agree that (a) you would like to participate in
            the Preview, (b) you have read, accept, and consent to the Terms and
            (c) you acknowledge the{' '}
            <a
              href="https://www.autodesk.com/company/legal-notices-trademarks/privacy-statement"
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
              onClick={() =>
                trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.TNC_LINK_CLICK, {
                  link: 'privacyStatement',
                })
              }>
              Autodesk Privacy Statement
            </a>
            , and agree to the use, processing, and storage of your personal
            data in accordance with the Autodesk Privacy Statement (including
            cross-border transfers as described therein). If You do not agree,
            You do not have the right to participate in the Preview or access
            any of products, services, and/or features provided hereunder.
          </p>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-border">
          {acceptError && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-destructive/5 border border-destructive/15 flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center">
                <span className="text-destructive text-base leading-none">
                  !
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  Something went wrong
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Unable to accept terms. Please try again.
                </p>
              </div>
            </div>
          )}

          {rejected && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-destructive/5 border border-destructive/15 flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center">
                <span className="text-destructive text-base leading-none">
                  !
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  You won&apos;t be able to use Autodesk Assistant Preview
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  You must accept the Preview Terms to continue using Autodesk
                  Assistant.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              size="lg"
              onClick={handleReject}
              disabled={isSubmitting}>
              Decline
            </Button>
            <Button
              size="lg"
              onClick={handleAccept}
              disabled={isSubmitting}
              className="px-8">
              Accept & Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
